import { getDeepObjectValue } from '@/utils/shared';
import { dynamicParameter } from './service/mixin-service';
import defaults from './configs/defaults';

export default function (name, path, emptyValue) {
  const getPath = dynamicParameter(path);
  const getEmptyValue = dynamicParameter(emptyValue);

  // Vue 2 warns if you add/delete dynamic properties on the instance proxy
  // Keep init state outside the instance and buffer writes until init finished
  const initStateByVm = new WeakMap();
  const pendingHasKey = `dataMixinIsPending_${name}`; // Required flag to allow falsy values during init buffering
  const pendingValueKey = `dataMixinPendingValue_${name}`;

  function setValue(vm, newValue) {
    const targetFieldPath = getPath(vm);
    const setWindowData = vm.$setValueAtTargetFieldPath ?? vm.setValueAtTargetFieldPath;
    setWindowData(targetFieldPath, newValue);
  }

  return {
    data() {
      return {
        [pendingHasKey]: false,
        [pendingValueKey]: undefined,
      };
    },
    inject: {
      _windowIdentifier: {
        default() {
          return undefined;
        }
      },
      $form: {
        default() {
          return undefined;
        }
      },
      $setValueAtTargetFieldPath: {
        default() {
          return undefined;
        }
      },
      _inMemoryStore: {
        default() {
          return undefined;
        }
      },
      $inMemoryStoreCreatedPromise: {
        default() {
          return undefined;
        }
      },
    },
    beforeCreate() {
      if (initStateByVm.has(this)) return;

      let resolveInit;
      const initPromise = new Promise((resolve) => {
        resolveInit = resolve;
      });

      const state = {
        ready: false,
        initPromise,
        resolveInit,
      };

      initStateByVm.set(this, state);

      // Always flush once init completes (if a pending write exists) and then cleanup
      initPromise.then(() => {
        if (this[pendingHasKey]) {
          const pending = this[pendingValueKey];
          this[pendingHasKey] = false;
          this[pendingValueKey] = undefined;
          setValue(this, pending);
        }

        initStateByVm.delete(this);
      });
    },
    computed: {
      [name]: {
        get() {
          // Return newest pending value if a write is buffered (can happen/only relevant when init not finished)
          if (this[pendingHasKey]) {
            const pending = this[pendingValueKey];
            return pending === undefined || pending === null
              ? getEmptyValue(this)
              : pending;
          }

          let targetFieldPath = getPath(this);
          let inMemoryStore = this._inMemoryStore ?? (('inMemoryStore' in this) ? this.inMemoryStore : undefined);
          let currentProp = inMemoryStore;

          currentProp = getDeepObjectValue(currentProp, targetFieldPath);

          return currentProp === undefined || currentProp === null
            ? getEmptyValue(this)
            : currentProp;
        },
        set(newValue) {
          const state = initStateByVm.get(this);

          // If init is finished (or no init state exists), write immediately
          if (!state || state.ready) {
            setValue(this, newValue);
            return;
          }

          // Buffer latest write; it will be flushed when init completes
          this[pendingValueKey] = newValue;
          this[pendingHasKey] = true;
        }
      }
    },
    async created() {
      const state = initStateByVm.get(this);

      try {
        // Wait for inMemoryStore to be initialized if available
        if (this.$inMemoryStoreCreatedPromise) {
          await this.$inMemoryStoreCreatedPromise();
        }

        if (!this._windowIdentifier && !this.windowIdentifier)
          window.console.warn(`Injection of windowIdentifier failed for property ${name}.`);

        if (this.$form?.isNew && getPath(this)) {
          let currentVal = this[name];
          if (currentVal !== undefined && currentVal !== null)
            return;

          const defaultValue = getEmptyValue(this) ?? defaults[this._windowIdentifier?.type]?.[getPath(this)];

          if (defaultValue === undefined)
            return;

          // Apply defaults during init (do not go through buffered setter)
          setValue(this, defaultValue);
        }
      } finally {
        if (state) state.ready = true;
        state?.resolveInit?.();
      }
    },
  }
}
